<template>
  <div class="language-selector" :class="{ open: isDropdownOpen }" @click.stop>
    <div
        class="selected-language"
        role="button"
        tabindex="0"
        @click="toggleDropdown"
        @keydown.enter.prevent="toggleDropdown"
        @keydown.space.prevent="toggleDropdown"
    >
      <span class="lang-left">
        <span class="lang-flag">
          <component :is="flagComponent(activeLanguage)" v-if="flagComponent(activeLanguage)" />
        </span>
        <span class="lang-title">{{ label(activeLanguage) }}</span>
      </span>
      <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M4 7L8.00001 11L12 7" stroke="#1C1C1C" stroke-linecap="round" stroke-linejoin="round"/>
      </svg>
    </div>

    <transition name="lang-dd">
      <div class="language-dropdown" v-if="isDropdownOpen">
        <div
            v-for="locale in dropdownLanguages"
            :key="locale"
            class="language-option"
            role="button"
            tabindex="0"
            @click="changeLocale(locale)"
            @keydown.enter.prevent="changeLocale(locale)"
        >
          <span class="lang-left">
            <span class="lang-flag">
              <component :is="flagComponent(locale)" v-if="flagComponent(locale)" />
            </span>
            <span class="lang-title">{{ label(locale) }}</span>
          </span>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import FlagEN from "@/components/icons/FlagEN.vue";
import FlagRU from "@/components/icons/FlagRU.vue";
import FlagUA from "@/components/icons/FlagUA.vue";
import FlagDE from "@/components/icons/FlagDE.vue";
import FlagES from "@/components/icons/FlagES.vue";
import FlagPT from "@/components/icons/FlagPT.vue";
import FlagPL from "@/components/icons/FlagPL.vue";

const FLAG_COMPONENTS = {
  EN: FlagEN,
  UA: FlagUA,
  ES: FlagES,
  RU: FlagRU,
  PT: FlagPT,
  PL: FlagPL,
  DE: FlagDE,
};

export default {
  name: "LanguageSelectorFlagged",
  data() {
    return {
      isDropdownOpen: false,
      myLangOrder: ["UA", "RU", "EN", "DE", "ES", "PT", "PL"],
      labels: {
        EN: "English",
        UA: "Українська",
        RU: "Русский",
        DE: "Deutsch",
        ES: "Español",
        PT: "Português",
        PL: "Polski",
      },
    };
  },
  computed: {
    activeLanguage() {
      return this.$i18n.locale;
    },
    availableLanguages() {
      const locales = Array.isArray(this.$i18n.availableLocales)
          ? [...this.$i18n.availableLocales]
          : [];

      locales.sort((a, b) => {
        const ai = this.myLangOrder.indexOf(a);
        const bi = this.myLangOrder.indexOf(b);
        return (ai === -1 ? 999 : ai) - (bi === -1 ? 999 : bi);
      });

      return locales;
    },
    dropdownLanguages() {
      return this.availableLanguages.filter((l) => l !== this.activeLanguage);
    },
  },
  unmounted() {
    this.isDropdownOpen = false;
  },
  methods: {
    toggleDropdown() {
      this.isDropdownOpen = !this.isDropdownOpen;
    },
    closeDropdown() {
      this.isDropdownOpen = false;
    },
    changeLocale(locale) {
      this.isDropdownOpen = false;
      this.$emit("change-locale", locale);
    },
    label(locale) {
      return this.labels[locale] || locale;
    },
    flagComponent(locale) {
      return FLAG_COMPONENTS[locale] || null;
    },
  },
};
</script>

<style scoped>
.language-selector {
  position: relative;
  display: block;
  width: 100%;
  height: 43px;
}

.selected-language {
  height: 43px;
  width: 100%;
  padding: 8px 12px;
  background: transparent;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: space-between;
  transition: background 0.2s ease;
  user-select: none;
}

.selected-language:hover {
  background: linear-gradient(to right, #FFFFFF, #ECEFFF);
}

.language-selector.open .selected-language {
  background: #fff;
  box-shadow: 0 1px 4px rgba(28, 28, 28, 0.08);
  border-radius: 8px 8px 0 0;
}

.lang-left {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
}

.lang-title {
  white-space: nowrap;
  font-weight: 400;
}

.lang-flag {
  width: 19px;
  height: 12px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
}

.language-dropdown {
  position: absolute;
  top: 43px;
  left: 0;
  width: 100%;
  z-index: 50;
  background: #fff;
  border-radius: 0 0 8px 8px;
  overflow: hidden;
  box-shadow: 0 1px 4px rgba(28, 28, 28, 0.08);
  transform-origin: top;
}

.language-option {
  width: 100%;
  text-align: left;
  background: #fff;
  padding: 12px 12px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 400;
  display: flex;
  align-items: center;
  user-select: none;
}

.language-option:hover {
  background: #EEF2FF;
}

.lang-dd-enter-active,
.lang-dd-leave-active {
  transition: opacity 0.18s ease, transform 0.18s ease;
}

.lang-dd-enter-from,
.lang-dd-leave-to {
  opacity: 0;
  transform: translateY(-6px);
}

.lang-dd-enter-to,
.lang-dd-leave-from {
  opacity: 1;
  transform: translateY(0);
}

.language-selector.open .selected-language:hover {
  background: #fff;
}
</style>

